import React, { useEffect, useState } from 'react';
import { Card, Table, Typography, Button, Space, Spin, message, Tag } from 'antd';
import { ArrowLeftOutlined, GiftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, DiscountWithUser } from '../utils/api';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

const DiscountOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [discounts, setDiscounts] = useState<DiscountWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 检查用户是否已登录且为医生
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    }
    if (!authState.isLoading && authState.isAuthenticated && !authState.user?.is_doctor) {
      message.error('您没有权限访问此页面');
      navigate('/');
    }
  }, [authState.isLoading, authState.isAuthenticated, authState.user, navigate]);

  // 加载优惠券列表
  const loadDiscounts = async (page: number, size: number) => {
    if (!authState.token) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.getAllDiscountList({ page, page_size: size }, authState.token);
      
      if (response.status === 200 && response.data) {
        setDiscounts(response.data.discounts || []);
        setTotal(response.data.total || 0);
        setCurrentPage(response.data.page || page);
        setPageSize(response.data.page_size || size);
      } else {
        message.error(response.msg || '获取优惠券列表失败');
      }
    } catch (error) {
      console.error('获取优惠券列表失败:', error);
      message.error('获取优惠券列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authState.isAuthenticated && authState.token && authState.user?.is_doctor) {
      loadDiscounts(currentPage, pageSize);
    }
  }, [authState.isAuthenticated, authState.token, authState.user]);

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    const { current, pageSize: newPageSize } = pagination;
    setCurrentPage(current || 1);
    setPageSize(newPageSize || 10);
    loadDiscounts(current || 1, newPageSize || 10);
  };

  // 返回主页
  const handleBack = () => {
    navigate('/');
  };

  // 格式化来源类型
  const formatSourceType = (sourceType: number) => {
    switch (sourceType) {
      case 1:
        return '首次使用';
      case 2:
        return '邀请用户';
      case 3:
        return '提出意见';
      default:
        return '未知';
    }
  };

  // 格式化时间
  const formatTime = (timeString: string) => {
    if (!timeString || timeString === '0001-01-01 00:00:00') return '-';
    const date = new Date(timeString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 表格列定义
  const columns: ColumnsType<DiscountWithUser> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: '来源类型',
      dataIndex: 'source_type',
      key: 'source_type',
      width: 120,
      render: (sourceType: number) => (
        <Tag color={sourceType === 1 ? 'blue' : sourceType === 2 ? 'green' : 'orange'}>
          {formatSourceType(sourceType)}
        </Tag>
      ),
    },
    {
      title: '来源详情',
      dataIndex: 'source_detail',
      key: 'source_detail',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'used',
      key: 'used',
      width: 100,
      render: (used: boolean) => (
        <Tag color={used ? 'default' : 'success'}>
          {used ? '已使用' : '未使用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => formatTime(time),
    },
    {
      title: '使用时间',
      dataIndex: 'used_at',
      key: 'used_at',
      width: 180,
      render: (time: string, record: DiscountWithUser) => 
        record.used ? formatTime(time) : '-',
    },
  ];

  // 如果正在加载
  if (authState.isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录或不是医生
  if (!authState.isAuthenticated || !authState.user?.is_doctor) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* 顶部导航栏 */}
      <div style={{
        width: '100%',
        maxWidth: '1400px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '40px'
      }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          size="large"
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          返回主页
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <GiftOutlined style={{ fontSize: '32px', color: 'white' }} />
          <Title level={2} style={{ color: 'white', margin: 0 }}>
            优惠券管理
          </Title>
        </div>
        <div style={{ width: '100px' }} /> {/* 占位符保持居中 */}
      </div>

      {/* 统计信息 */}
      <div style={{
        width: '100%',
        maxWidth: '1400px',
        marginBottom: '24px'
      }}>
        <Space size="large">
          <Card style={{ borderRadius: '8px' }}>
            <Text strong>总优惠券数：</Text>
            <Text style={{ fontSize: '24px', color: '#1890ff', marginLeft: '8px' }}>
              {total}
            </Text>
          </Card>
          <Card style={{ borderRadius: '8px' }}>
            <Text strong>未使用：</Text>
            <Text style={{ fontSize: '24px', color: '#52c41a', marginLeft: '8px' }}>
              {discounts.filter(d => !d.used).length}
            </Text>
          </Card>
          <Card style={{ borderRadius: '8px' }}>
            <Text strong>已使用：</Text>
            <Text style={{ fontSize: '24px', color: '#999', marginLeft: '8px' }}>
              {discounts.filter(d => d.used).length}
            </Text>
          </Card>
        </Space>
      </div>

      {/* 优惠券列表 */}
      <Card 
        style={{
          width: '100%',
          maxWidth: '1400px',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Table
          columns={columns}
          dataSource={discounts}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default DiscountOverviewPage;

